import React, { useEffect, useState } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./UPIMandate.css";

const subscriptionAmount = 1;

const UPIMandate = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const navbar = document.querySelector("header, .navbar");
    const footer = document.querySelector("footer");

    if (navbar) navbar.style.display = "none";
    if (footer) footer.style.display = "none";

    return () => {
      if (navbar) navbar.style.display = "";
      if (footer) footer.style.display = "";
    };
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState("");

  const [mandateStatus, setMandateStatus] = useState(null);
  const [mandateData, setMandateData] = useState(null);

  const [showSetupForm, setShowSetupForm] = useState(true);

  // =====================================================
  // CHECK EXISTING UPI MANDATE
  // =====================================================

  useEffect(() => {
    const checkExistingMandate = async () => {
      try {
        const subscriptionId = localStorage.getItem(
          "upiMandateSubscriptionId"
        );

        const savedName = localStorage.getItem(
          "upiMandateName"
        );

        const savedEmail = localStorage.getItem(
          "upiMandateEmail"
        );

        if (savedName || savedEmail) {
          setFormData((prev) => ({
            ...prev,
            name: savedName || prev.name,
            email: savedEmail || prev.email,
          }));
        }

        if (!subscriptionId) {
          setCheckingStatus(false);
          setShowSetupForm(true);
          return;
        }

        const response = await axios.get(
          `/api/razorpay/upi-mandate/${subscriptionId}`
        );

        console.log(
          "Existing UPI Mandate Status:",
          response.data
        );

        if (!response.data?.success) {
          setCheckingStatus(false);
          setShowSetupForm(true);
          return;
        }

        const data =
          response.data.mandate ||
          response.data.data ||
          response.data;

        const status =
          data?.status ||
          response.data?.status;

        setMandateData(data);
        setMandateStatus(status);

        if (status) {
          localStorage.setItem(
            "upiMandateStatus",
            status
          );
        }

        if (
          status === "active" ||
          status === "authenticated"
        ) {
          setShowSetupForm(false);
        } else if (
          status === "cancelled" ||
          status === "completed" ||
          status === "expired"
        ) {
          setShowSetupForm(false);
        } else {
          setShowSetupForm(true);
        }
      } catch (err) {
        console.error(
          "Existing UPI mandate check failed:",
          err
        );

        setShowSetupForm(true);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkExistingMandate();
  }, []);

  // =====================================================
  // FORM HANDLERS
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({
      ...prev,
      phone,
    }));

    setError("");
  };

  // =====================================================
  // RAZORPAY SCRIPT
  // =====================================================

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");

      script.src =
        "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => resolve(true);

      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  // =====================================================
  // SETUP UPI AUTOPAY
  // =====================================================

  const handleSetupMandate = async () => {
    setError("");

    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();

    if (!name) {
      setError("Please enter your name.");
      return;
    }

    if (!email) {
      setError("Please enter your email.");
      return;
    }

    if (!phone) {
      setError("Please enter your phone number.");
      return;
    }

    try {
      setLoading(true);

      // =================================================
      // LOAD RAZORPAY
      // =================================================

      const razorpayLoaded =
        await loadRazorpayScript();

      if (!razorpayLoaded) {
        throw new Error(
          "Razorpay Checkout failed to load. Please try again."
        );
      }

      // =================================================
      // CREATE RAZORPAY SUBSCRIPTION
      // =================================================

      const response = await axios.post(
        "/api/razorpay/create-upi-mandate",
        {
          name,
          email,
          phone,
        }
      );

      const data = response.data;

      console.log(
        "UPI Mandate Response:",
        data
      );

      if (!data?.success) {
        throw new Error(
          data?.message ||
            "Unable to create UPI AutoPay mandate."
        );
      }

      const subscriptionId =
        data?.subscriptionId ||
        data?.mandate?.razorpaySubscriptionId;

      const keyId =
        data?.keyId ||
        data?.razorpayKeyId ||
        data?.key;

      if (!subscriptionId) {
        throw new Error(
          "Razorpay subscription ID was not received."
        );
      }

      if (!keyId) {
        throw new Error(
          "Razorpay Key ID was not received."
        );
      }

      // =================================================
      // SAVE TEMPORARY SUBSCRIPTION DETAILS
      // =================================================

      localStorage.setItem(
        "upiMandateSubscriptionId",
        subscriptionId
      );

      localStorage.setItem(
        "upiMandateEmail",
        email.toLowerCase()
      );

      localStorage.setItem(
        "upiMandateName",
        name
      );

      localStorage.setItem(
        "upiMandateStatus",
        "created"
      );

      // =================================================
      // RAZORPAY CHECKOUT
      // =================================================

      const options = {
        key: keyId,

        subscription_id: subscriptionId,

        name: "Forge Your Peak",

        description: "Monthly Subscription",

        image: "/logo.png",

        prefill: {
          name,
          email,
          contact: phone,
        },

        notes: {
          service: "Forge Your Peak",
          paymentMethod: "UPI_AUTOPAY",
        },

        theme: {
          color: "#111827",
        },

        // =================================================
        // PAYMENT SUCCESS
        // =================================================

        handler: async function (paymentResponse) {
          console.log(
            "Razorpay UPI AutoPay Response:",
            paymentResponse
          );

          try {
            // =================================================
            // VERIFY PAYMENT
            //
            // IMPORTANT:
            // name, email, phone are also sent here.
            //
            // Backend:
            // verify → create UPIMandate → TagMango
            // =================================================

            const verifyResponse =
              await axios.post(
                "/api/razorpay/verify-upi-autopay",
                {
                  razorpayPaymentId:
                    paymentResponse.razorpay_payment_id,

                  razorpaySubscriptionId:
                    paymentResponse.razorpay_subscription_id ||
                    subscriptionId,

                  razorpaySignature:
                    paymentResponse.razorpay_signature,

                  // CUSTOMER DETAILS
                  name,
                  email,
                  phone,
                }
              );

            console.log(
              "UPI AutoPay Verification:",
              verifyResponse.data
            );

            // =================================================
            // VERIFICATION SUCCESS
            // =================================================

            if (verifyResponse.data?.success) {
              /*
                Backend has now:

                1. Verified Razorpay payment
                2. Created UPIMandate
                3. Registered user in TagMango

                TagMango failure will not make
                payment verification fail.
              */

              localStorage.setItem(
                "upiMandateStatus",
                "authenticated"
              );

              localStorage.setItem(
                "upiMandateSubscriptionId",
                subscriptionId
              );

              localStorage.setItem(
                "upiMandateName",
                name
              );

              localStorage.setItem(
                "upiMandateEmail",
                email.toLowerCase()
              );

              setMandateStatus("authenticated");

              setShowSetupForm(false);

              // =================================================
              // GET LATEST STATUS
              // =================================================

              try {
                const statusResponse =
                  await axios.get(
                    `/api/razorpay/upi-mandate/${subscriptionId}`
                  );

                const latestData =
                  statusResponse.data?.mandate ||
                  statusResponse.data?.data ||
                  statusResponse.data;

                const latestStatus =
                  latestData?.status ||
                  statusResponse.data?.status;

                setMandateData(
                  latestData
                );

                setMandateStatus(
                  latestStatus ||
                    "authenticated"
                );

                setShowSetupForm(false);

                if (latestStatus) {
                  localStorage.setItem(
                    "upiMandateStatus",
                    latestStatus
                  );
                }
              } catch (statusError) {
                console.error(
                  "Unable to refresh mandate status:",
                  statusError
                );

                setMandateStatus(
                  "authenticated"
                );

                setShowSetupForm(false);
              }
            } else {
              setError(
                verifyResponse.data?.message ||
                  "UPI AutoPay verification failed."
              );
            }
          } catch (verifyError) {
            console.error(
              "UPI AutoPay verification error:",
              verifyError
            );

            setError(
              verifyError?.response?.data?.message ||
                verifyError?.message ||
                "Payment verification failed."
            );
          } finally {
            setLoading(false);
          }
        },

        // =================================================
        // CHECKOUT CLOSED
        // =================================================

        modal: {
          ondismiss: function () {
            console.log(
              "Razorpay UPI AutoPay checkout closed."
            );

            setLoading(false);
          },
        },
      };

      const razorpay =
        new window.Razorpay(options);

      // =================================================
      // PAYMENT FAILED
      // =================================================

      razorpay.on(
        "payment.failed",
        function (response) {
          console.error(
            "Razorpay payment failed:",
            response
          );

          setError(
            response?.error?.description ||
              "UPI AutoPay setup failed. Please try again."
          );

          setLoading(false);
        }
      );

      razorpay.open();
    } catch (err) {
      console.error(
        "UPI AutoPay setup error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to setup UPI AutoPay."
      );

      setLoading(false);
    }
  };

  // =====================================================
  // CANCEL UPI AUTOPAY
  // =====================================================

  const handleCancelMandate = async () => {
    const subscriptionId =
      localStorage.getItem(
        "upiMandateSubscriptionId"
      );

    if (!subscriptionId) {
      setError(
        "Subscription ID not found."
      );
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to cancel UPI AutoPay?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await axios.post(
        `/api/razorpay/upi-mandate/${subscriptionId}/cancel`
      );

      console.log(
        "Cancel UPI Mandate Response:",
        response.data
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
            "Unable to cancel UPI AutoPay."
        );
      }

      setMandateStatus("cancelled");

      setMandateData((prev) => ({
        ...prev,
        status: "cancelled",
      }));

      localStorage.setItem(
        "upiMandateStatus",
        "cancelled"
      );

      setShowSetupForm(false);
    } catch (err) {
      console.error(
        "Cancel UPI mandate error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to cancel UPI AutoPay."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // SETUP AGAIN
  // =====================================================

  const handleSetupAgain = () => {
    setMandateStatus(null);
    setMandateData(null);
    setShowSetupForm(true);
    setError("");

    /*
      Old subscription ID is intentionally not removed.

      New subscription ID will replace it after
      create-upi-mandate succeeds.
    */
  };

  // =====================================================
  // LOADING SCREEN
  // =====================================================

  if (checkingStatus) {
    return (
      <div className="mandate-page">
        <header className="mandate-header">
          <div className="brand">
            <div className="brand-text">
              <h2>Forge Your Peak</h2>
            </div>
          </div>
        </header>

        <main className="mandate-container">
          <section className="payment-card mandate-status-card">
            <div className="status-loader">
              <div className="loader-circle"></div>
            </div>

            <h2>
              Checking Your Subscription
            </h2>

            <p className="payment-subtitle">
              Please wait while we check your
              UPI AutoPay status.
            </p>
          </section>
        </main>
      </div>
    );
  }

  // =====================================================
  // STATUS PAGE
  // =====================================================

  if (
    !showSetupForm &&
    mandateStatus
  ) {
    const isActive =
      mandateStatus === "active" ||
      mandateStatus === "authenticated";

    const isCancelled =
      mandateStatus === "cancelled";

    const isCompleted =
      mandateStatus === "completed";

    const isExpired =
      mandateStatus === "expired";

    return (
      <div className="mandate-page">
        <main className="mandate-container">

          <section className="mandate-left">

            <div className="top-badge">
              <span>SIMPLE</span>
              <b>•</b>
              <span>SECURE</span>
              <b>•</b>
              <span>CANCEL ANYTIME</span>
            </div>

            <h1>
              Simple Payments.
              <br />
              Easy Management.
            </h1>

            <p className="hero-description">
              Manage your UPI AutoPay subscription from here.
            </p>

            <div className="benefits">

              <div className="benefit">
                <div className="benefit-icon">
                  ▣
                </div>

                <div className="benefit-content">
                  <h3>
                    Automatic Payments
                  </h3>

                  <p>
                    No manual payments every month
                  </p>
                </div>
              </div>

              <div className="benefit">
                <div className="benefit-icon">
                  ⌑
                </div>

                <div className="benefit-content">
                  <h3>
                    Secure & Trusted
                  </h3>

                  <p>
                    Powered by Razorpay UPI AutoPay
                  </p>
                </div>
              </div>

              <div className="benefit">
                <div className="benefit-icon">
                  ×
                </div>

                <div className="benefit-content">
                  <h3>
                    You're in Control
                  </h3>

                  <p>
                    Manage your subscription anytime
                  </p>
                </div>
              </div>

            </div>
          </section>

          <section className="payment-card mandate-status-card">

            <div
              className={`status-icon ${
                isActive
                  ? "status-active"
                  : "status-inactive"
              }`}
            >
              {isActive ? "✓" : "!"}
            </div>

            <h2>
              UPI AutoPay
            </h2>

            {/* ================= ACTIVE ================= */}

            {isActive && (
              <>
                <div className="status-label active-label">
                  Status: Active ✓
                </div>

                <p className="payment-subtitle">
                  Your UPI AutoPay mandate is
                  active.
                </p>

                <div className="status-amount-box">

                  <strong>
                    ₹{subscriptionAmount}
                  </strong>

                  <span>
                    / Month
                  </span>

                  <p>
                    3 monthly payments
                  </p>

                </div>

                {mandateData?.nextChargeAt && (
                  <div className="next-payment">

                    <span>
                      Next payment
                    </span>

                    <strong>
                      {new Date(
                        mandateData.nextChargeAt
                      ).toLocaleDateString(
                        "en-IN",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }
                      )}
                    </strong>

                  </div>
                )}

                {error && (
                  <div className="mandate-error">
                    {error}
                  </div>
                )}

                <button
                  className="cancel-button"
                  onClick={
                    handleCancelMandate
                  }
                  disabled={loading}
                >
                  {loading
                    ? "Cancelling..."
                    : "Cancel AutoPay"}
                </button>

                <div className="secure-payment">

                  <span>🔒</span>

                  <span>
                    Your payment details are
                    securely handled by Razorpay
                  </span>

                </div>
              </>
            )}

            {/* ================= CANCELLED ================= */}

            {isCancelled && (
              <>
                <div className="status-label cancelled-label">
                  Status: Cancelled
                </div>

                <p className="payment-subtitle">
                  Your UPI AutoPay mandate has
                  been cancelled.
                </p>

                <div className="cancelled-box">

                  <div>
                    ✓
                  </div>

                  <p>
                    No further automatic
                    payments will be made.
                  </p>

                </div>

                {error && (
                  <div className="mandate-error">
                    {error}
                  </div>
                )}

                <button
                  className="setup-button"
                  onClick={
                    handleSetupAgain
                  }
                >
                  <span>
                    Setup UPI AutoPay Again
                  </span>

                  <span className="arrow">
                    →
                  </span>
                </button>
              </>
            )}

            {/* ================= COMPLETED ================= */}

            {isCompleted && (
              <>
                <div className="status-label completed-label">
                  Status: Completed
                </div>

                <p className="payment-subtitle">
                  Your 3 monthly AutoPay
                  payments have been completed.
                </p>

                <div className="cancelled-box">

                  <div>
                    ✓
                  </div>

                  <p>
                    This subscription has
                    completed all scheduled
                    payments.
                  </p>

                </div>

                <button
                  className="setup-button"
                  onClick={
                    handleSetupAgain
                  }
                >
                  <span>
                    Start New Subscription
                  </span>

                  <span className="arrow">
                    →
                  </span>
                </button>
              </>
            )}

            {/* ================= EXPIRED ================= */}

            {isExpired && (
              <>
                <div className="status-label cancelled-label">
                  Status: Expired
                </div>

                <p className="payment-subtitle">
                  Your UPI AutoPay mandate has
                  expired.
                </p>

                <button
                  className="setup-button"
                  onClick={
                    handleSetupAgain
                  }
                >
                  <span>
                    Setup New AutoPay
                  </span>

                  <span className="arrow">
                    →
                  </span>
                </button>
              </>
            )}

          </section>
        </main>
      </div>
    );
  }

  // =====================================================
  // NEW SUBSCRIPTION SETUP PAGE
  // =====================================================

  return (
    <div className="mandate-page">

      <main className="mandate-container">

        {/* =================================================
            LEFT SIDE
        ================================================= */}

        <section className="mandate-left">

          <div className="top-badge">
            <span>SIMPLE</span>
            <b>•</b>
            <span>SECURE</span>
            <b>•</b>
            <span>CANCEL ANYTIME</span>
          </div>

          <h1>
            Set Up Your Subscription.
            <br />
            Pay Automatically.
          </h1>

          <p className="hero-description">
            Set up a simple monthly plan powered by UPI
            AutoPay.
          </p>

          <div className="benefits">

            <div className="benefit">

              <div className="benefit-icon">
                ▣
              </div>

              <div className="benefit-content">

                <h3>
                  Automatic Payments
                </h3>

                <p>
                  No manual payments every month
                </p>

              </div>

            </div>

            <div className="benefit">

              <div className="benefit-icon">
                ⌑
              </div>

              <div className="benefit-content">

                <h3>
                  Secure & Trusted
                </h3>

                <p>
                  Powered by Razorpay UPI AutoPay
                </p>

              </div>

            </div>

            <div className="benefit">

              <div className="benefit-icon">
                ×
              </div>

              <div className="benefit-content">

                <h3>
                  Cancel Anytime
                </h3>

                <p>
                  You're in control
                </p>

              </div>

            </div>

            <div className="benefit">

              <div className="benefit-icon">
                ▥
              </div>

              <div className="benefit-content">

                <h3>
                  Easy Management
                </h3>

                <p>
                  Manage your subscription anytime
                </p>

              </div>

            </div>

          </div>

          <div className="quote-box">

            <span className="quote-mark">
              “
            </span>

            <div>

              <p>
                Simple setup. Secure payments.
                Complete control.
              </p>

              <span>
                – Forge Your Peak
              </span>

            </div>

          </div>

        </section>

        {/* =================================================
            PAYMENT CARD
        ================================================= */}

        <section className="payment-card">

          <h2>
            Complete Your Subscription
          </h2>

          <p className="payment-subtitle">
            Set up UPI AutoPay and get started
            in seconds.
          </p>

          {/* AMOUNT */}

          <div className="amount-box">

            <div className="amount">

              ₹{subscriptionAmount}

              <span>
                / Month
              </span>

            </div>

            <p>
              (Testing Amount)
            </p>

          </div>

          {/* CUSTOMER DETAILS */}

          <div className="billing-fields">

            {/* NAME */}

            <div className="form-group">

              <label htmlFor="name">
                Full Name
              </label>

              <input
                id="name"
                type="text"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
              />

            </div>

            {/* EMAIL */}

            <div className="form-group">

              <label htmlFor="email">
                Email Address
              </label>

              <input
                id="email"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />

            </div>

            {/* PHONE */}

            <div className="form-group">

              <label>
                Phone Number
              </label>

              <PhoneInput
                country="in"
                value={formData.phone}
                onChange={
                  handlePhoneChange
                }
                enableSearch
                countryCodeEditable={false}
                disabled={loading}
                inputProps={{
                  name: "phone",
                  required: true,
                }}
              />

            </div>

          </div>

          {/* ERROR */}

          {error && (
            <div className="mandate-error">
              {error}
            </div>
          )}

          {/* AUTOPAY INFO */}

          <div className="autopay-info">

            <div className="autopay-icon">
              ⚡
            </div>

            <div>

              <h3>
                UPI AutoPay Mandate
              </h3>

              <p>
                You will be redirected to your
                UPI app to approve the mandate.
              </p>

            </div>

          </div>

          {/* SETUP BUTTON */}

          <button
            className="setup-button"
            onClick={
              handleSetupMandate
            }
            disabled={loading}
          >

            <span>
              {loading
                ? "Setting Up..."
                : "Setup UPI AutoPay"}
            </span>

            {!loading && (
              <span className="arrow">
                →
              </span>
            )}

          </button>

          {/* POWERED BY */}

          <div className="powered-section">

            <div className="powered-line"></div>

            <span>
              Powered by
            </span>

            <div className="powered-line"></div>

          </div>

          <div className="payment-providers">

            <strong className="razorpay-text">
              Razorpay
            </strong>

            <strong className="upi-text">
              UPI
            </strong>

            <span className="upi-app gpay">
              G
            </span>

            <span className="upi-app phonepe">
              पे
            </span>

            <span className="upi-app paytm">
              Paytm
            </span>

          </div>

          {/* SECURE */}

          <div className="secure-payment">

            <span>
              🔒
            </span>

            <span>
              100% Secure Payments
            </span>

          </div>

        </section>

      </main>

    </div>
  );
};

export default UPIMandate;